/**
 * My Votes Page
 *
 * Story FOS-4.2.8: ProposalList & Detail Enhancement
 * Task 8: Create MyVotesPage
 *
 * Displays user's voting history with proposal titles and choices.
 *
 * @see AC-4.2.8.4 - All user's past votes displayed with choices
 *
 * @module pages/Governance/MyVotesPage
 */

import { useState, useEffect, useCallback } from 'react';
import { Loader2, AlertCircle } from 'lucide-react';
import { useAuth } from '@hello-world-dao/auth';
import { ErrorBoundary } from '../../components/ErrorBoundary';
import { MyVotesHistory } from '../../components/governance';
import {
  getMyVotes,
  isGovernanceError,
  GovernanceErrorType,
  type VoteHistoryItem,
} from '../../services/governanceService';

// =============================================================================
// Component
// =============================================================================

/**
 * My votes page
 */
export function MyVotesPage() {
  const { user } = useAuth();

  // Data state
  const [votes, setVotes] = useState<VoteHistoryItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // ==========================================================================
  // Data Fetching
  // ==========================================================================

  const loadVotes = useCallback(async () => {
    if (!user?.icPrincipal) {
      setVotes([]);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const data = await getMyVotes(user.icPrincipal);
      // BL-1057: defensive — state must always hold an array so MyVotesHistory
      // can never crash on `votes.length` (the ErrorBoundary bug this fixes).
      setVotes(Array.isArray(data) ? data : []);
    } catch (e) {
      // Check if it's a real governance error (not network)
      if (isGovernanceError(e) && e.type !== GovernanceErrorType.NETWORK_ERROR) {
        setError(e.message);
        setIsLoading(false);
        return;
      }

      // No data available when API is not reachable
      setVotes([]);
    } finally {
      setIsLoading(false);
    }
  }, [user]);

  useEffect(() => {
    loadVotes();
  }, [loadVotes]);

  // ==========================================================================
  // Render
  // ==========================================================================

  return (
    <ErrorBoundary>
      <div data-testid="my-votes-page">
        {/* Header */}
        <div className="mb-6">
          <h2 className="text-lg font-semibold text-foreground">
            My Voting History
          </h2>
          <p className="text-sm text-muted-foreground">
            Your complete record of governance participation
          </p>
        </div>

        {/* Content */}
        {isLoading ? (
          <div className="flex items-center justify-center py-12" data-testid="my-votes-loading">
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
          </div>
        ) : error ? (
          <div className="py-8" data-testid="my-votes-error">
            <div className="flex items-center gap-3 p-4 bg-red-50 dark:bg-red-900/20 rounded-lg border border-red-200 dark:border-red-800">
              <AlertCircle className="w-5 h-5 text-red-500" />
              <p className="text-red-700 dark:text-red-300">{error}</p>
            </div>
          </div>
        ) : (
          <MyVotesHistory votes={votes} />
        )}
      </div>
    </ErrorBoundary>
  );
}

export default MyVotesPage;
